import {
    parser,
    replaceNodes,
    render,
    iterateAst,
    AstNodeType,
    analyzer,
    recurseParen,
} from '@thinkalpha/language-services';
import type { KnownAstNode } from '@thinkalpha/language-services';
import type {
    BespokeIndicatorImport,
    IndicatorFormula,
    IndicatorImportRef,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { LRUCache } from 'lru-cache';
import type { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import type { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { container } from 'src/StaticContainer';
import { v4 } from 'uuid';

const cachedBespokeIndicators = new LRUCache</* stringified IndicatorFormula */ string, BespokeIndicatorImport>({
    max: 1000,
});

const log = container.get('Logger').getSubLogger({ name: 'feat:validationMode' });

function uuidToName(id: string): string {
    return '_' + id.toString().replaceAll('-', '');
}

const generateName = () => uuidToName(v4());

const getReferencedImports = (node: KnownAstNode, imports: IndicatorImportRef[]): IndicatorImportRef[] => {
    return iterateAst(node)
        .filter((node) => node.type === AstNodeType.call)
        .reduce((acc, node) => {
            const importViewModel = imports.find(
                (importViewModel) => importViewModel.alias.toLowerCase() === node.source.toLowerCase(),
            );
            if (importViewModel) {
                acc.push(importViewModel);
            }
            return acc;
        }, [] as IndicatorImportRef[]);
};

const createSyntheticCallNode = (alias: string): KnownAstNode => ({
    type: AstNodeType.call,
    source: alias,
    arguments: [],
    errors: [],
    warnings: [],
    path: '',
    ranges: {
        eachArgument: [],
        node: { start: 0, end: 0 },
        source: { start: 0, end: 0 },
        openParen: { start: 0, end: 0 },
        closeParen: { start: 0, end: 0 },
    },
    valid: true,
});

export async function strategyGenerator(
    plan: SearchPlanDTO,
    imports: IndicatorImportModel[],
): Promise<[BespokeIndicatorImport[], IndicatorFormula] | null> {
    // Render the strategy to a formula
    const results = await plan.renderToFormula();
    if (!results) return null;
    const { formula } = results;

    let parserResult = parser(formula, {});

    if (!parserResult.root) {
        return null;
    }

    const root = analyzer(parserResult.root, {
        functionDefs: imports.map((i) => i.toDictionaryFunctionDef()),
    });

    parserResult = {
        ...parserResult,
        root,
    };

    if (!parserResult.root) {
        return null;
    }

    const replacements: WeakMap<KnownAstNode, KnownAstNode> = new WeakMap();

    const bespokeIndicators: BespokeIndicatorImport[] = [];

    const tryExtractBespokeIndicator = (operand: KnownAstNode | null): KnownAstNode | BespokeIndicatorImport | null => {
        if (!operand) return operand;
        if (!operand.dataType) {
            log.warn({ message: 'Node is missing dataType', operand });
            return operand;
        }
        if (operand.dataType === 'boolean') return operand;
        if (recurseParen(operand)?.type === AstNodeType.const) return operand;

        const indicatorFormula: IndicatorFormula = {
            formula: render(operand),
            imports: getReferencedImports(operand, plan.imports),
        };

        const indicatorFormulaKey = JSON.stringify(indicatorFormula);
        const cached = cachedBespokeIndicators.get(indicatorFormulaKey);
        if (cached) {
            if (!bespokeIndicators.includes(cached)) {
                bespokeIndicators.push(cached);
            }
            return cached;
        }

        const indicator: BespokeIndicatorImport = {
            id: v4(),
            formula: indicatorFormula,
            dataType: operand.dataType,
            alias: generateName(),
            unit: operand.unit ?? null,
            rowKey: 'ticker',
        };
        cachedBespokeIndicators.set(indicatorFormulaKey, indicator);
        bespokeIndicators.push(indicator);
        return indicator;
    };

    const toAstNode = (extracted: KnownAstNode | BespokeIndicatorImport | null): KnownAstNode | null => {
        if (extracted === null) return null;
        if ('type' in extracted) return extracted;
        return createSyntheticCallNode(extracted.alias);
    };

    for (const node of iterateAst(parserResult.root)) {
        if (node?.dataType !== 'boolean') continue;

        switch (node.type) {
            case AstNodeType.binaryOperation: {
                const lhs = tryExtractBespokeIndicator(node.lhs);
                const rhs = tryExtractBespokeIndicator(node.rhs);

                const newNode: KnownAstNode = {
                    ...node,
                    lhs: toAstNode(lhs) ?? node.lhs,
                    rhs: toAstNode(rhs) ?? node.rhs,
                };

                replacements.set(node, newNode);
                break;
            }
            case AstNodeType.call: {
                const newArguments = node.arguments.map(
                    (arg, i) => toAstNode(tryExtractBespokeIndicator(arg)) ?? node.arguments[i],
                );

                const changed = newArguments.some((arg, i) => arg !== node.arguments[i]);
                if (!changed) break;

                const newNode: KnownAstNode = {
                    ...node,
                    arguments: newArguments,
                };

                replacements.set(node, newNode);
                break;
            }
        }
    }

    const replaced = replaceNodes(parserResult.root, (node) => {
        if (!node) return node;

        if (replacements.has(node)) {
            return replacements.get(node)!;
        }
        return node;
    });

    return [
        bespokeIndicators,
        {
            formula: render(replaced),
            imports: [...plan.imports.map((i) => i.toContractType()), ...bespokeIndicators],
        },
    ];
}
